/**
 * Footer Component
 * Newsletter subscription stores emails in the "Newsletter" collection in MongoDB
 * Fields stored: email, subscribedAt, isActive, source, createdAt, updatedAt
 */

"use client";
import React, { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { toast } from "react-hot-toast";
import {
  EnvelopeIcon,
  PhoneIcon,
  ArrowRightIcon,
  CheckIcon,
  ChevronDownIcon,
  ExclamationCircleIcon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline";
import { useAuthSession } from "@/hooks/useAuthSession";

// Social Media Icons
const socialLinks = [
  {
    name: "Instagram",
    icon: (
      <svg viewBox="0 0 24 24" className="w-full h-full">
        <defs>
          <linearGradient
            id="instagram-gradient"
            x1="0%"
            y1="0%"
            x2="100%"
            y2="100%"
          >
            <stop offset="0%" stopColor="#f09433" />
            <stop offset="25%" stopColor="#e6683c" />
            <stop offset="50%" stopColor="#dc2743" />
            <stop offset="75%" stopColor="#cc2366" />
            <stop offset="100%" stopColor="#bc1888" />
          </linearGradient>
        </defs>
        <path
          fill="url(#instagram-gradient)"
          d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"
        />
        <circle cx="12" cy="12" r="4" fill="white" />
        <circle cx="18.406" cy="5.595" r="1.44" fill="white" />
      </svg>
    ),
    url: "https://instagram.com/relaxing.routines",
  },
];

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const footerSections = [
  {
    title: "Company",
    links: [
      { label: "About Us", href: "/about-us" },
      { label: "Our Instructors", href: "/instructors" },
      { label: "Blog & Articles", href: "/blogs" },
    ],
  },
  {
    title: "For Students",
    links: [
      { label: "Browse Classes", href: "/classes" },
      { label: "Prana AI Assistant", href: "/ai-assistant" },
      { label: "Student Registration", href: "/register" },
      { label: "Contact Support", href: "/contact-us" },
    ],
  },
  {
    title: "For Instructors",
    links: [
      { label: "Become an Instructor", href: "/become-instructor" },
      { label: "Instructor Login", href: "/instructors" },
      { label: "Instructor Registration", href: "/instructors/register" },
      { label: "Instructor Resources", href: "/about-us" },
    ],
  },
];

export function Footer() {
  const { user } = useAuthSession();
  const isStudent = user?.role === "STUDENT";

  const [email, setEmail] = useState("");
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [openSections, setOpenSections] = useState<{ [key: string]: boolean }>({
    Company: false,
    "For Students": false,
    "For Instructors": false,
  });

  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showInstallButton, setShowInstallButton] = useState(false);

  // PWA Install Prompt Handler
  React.useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setShowInstallButton(true);
      console.log("💾 PWA Install prompt available (Footer)");
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallApp = async () => {
    if (!deferredPrompt) {
      toast.error("Install prompt not ready. Try using Chrome or Edge, and ensure you are on HTTPS or localhost.");
      return;
    }

    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === "accepted") {
        toast.success("App installed successfully!");
        console.log("✅ User accepted the PWA install");
      } else {
        console.log("❌ User dismissed the PWA install");
      }

      setDeferredPrompt(null);
      setShowInstallButton(false);
    } catch (error) {
      console.error("Install error:", error);
    }
  };

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      setErrorMessage("Please enter your email");
      return;
    }

    setIsLoading(true);
    setErrorMessage("");

    try {
      const response = await fetch("/api/newsletter/subscribe", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          source: "footer",
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setIsSubscribed(true);
        setTimeout(() => {
          setIsSubscribed(false);
          setEmail("");
        }, 3000);
      } else {
        setErrorMessage(data.error || "Failed to subscribe. Please try again.");
        setTimeout(() => {
          setErrorMessage("");
        }, 4000);
      }
    } catch (error) {
      console.error("Newsletter subscription error:", error);
      setErrorMessage("Network error. Please check your connection.");
      setTimeout(() => {
        setErrorMessage("");
      }, 4000);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleSection = (title: string) => {
    setOpenSections((prev) => ({
      ...prev,
      [title]: !prev[title],
    }));
  };

  return (
    <footer className="relative bg-gradient-to-b from-white to-orange-50/30 pt-4 md:pt-16 pb-24 md:pb-8 border-t border-gray-100">
      <div className="container mx-auto px-4 zoom-container">
        <div className={`grid grid-cols-1 md:grid-cols-2 ${isStudent ? "lg:grid-cols-4" : "lg:grid-cols-5"} gap-8 lg:gap-4 mb-12 md:mb-16`}>
          {/* Brand Section */}
          <div className="space-y-6 align-center">
            <Link href="/" className="block">
              <Image
                src="/images/icon-logo.png"
                alt="Relaxing Routine"
                width={80}
                height={80}
                className="w-[80px] sm:w-[80px] h-auto"
                unoptimized
              />
            </Link>
            <p className="text-gray-600 text-sm leading-relaxed">
              Relaxing Routine is a wellness marketplace that connects students with independent wellness instructors and guided practices, supported by Prana AI powered recommendations to help build healthier routines at your own pace.
            </p>
          </div>

          {/* Links Sections - 2 columns on mobile, individual columns on desktop */}
          {footerSections.filter(section => !isStudent || section.title !== "For Instructors").map((section) => (
            <div key={section.title} className="space-y-4 md:space-y-6">
              {/* Mobile: Clickable header with dropdown */}
              <div className="md:hidden">
                <button
                  onClick={() => toggleSection(section.title)}
                  className="flex items-center justify-between w-full text-left"
                >
                  <h3 className="text-[#463a33] font-bold text-base relative inline-block">
                    {section.title}
                    <span className="absolute -bottom-2 left-0 w-8 h-1 bg-[#ff7043]/30 rounded-full"></span>
                  </h3>
                  <ChevronDownIcon
                    className={`w-5 h-5 text-[#463a33] transition-transform duration-300 ${openSections[section.title] ? "rotate-180" : ""
                      }`}
                  />
                </button>
                {openSections[section.title] && (
                  <ul className="space-y-3 mt-4 animate-slideDown">
                    {section.links.map((link) => (
                      <li key={link.label}>
                        <Link
                          href={link.href}
                          className="text-gray-600 hover:text-[#ff7043] transition-colors duration-300 flex items-center group text-sm"
                        >
                          <span className="w-0 group-hover:w-2 h-0.5 bg-[#ff7043] mr-0 group-hover:mr-2 transition-all duration-300"></span>
                          {link.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Desktop: Always visible */}
              <div className="hidden md:block">
                <h3 className="text-[#463a33] font-bold text-lg relative inline-block">
                  {section.title}
                  <span className="absolute -bottom-2 left-0 w-8 h-1 bg-[#ff7043]/30 rounded-full"></span>
                </h3>
                <ul className="space-y-4 mt-6">
                  {section.links.map((link) => (
                    <li key={link.label}>
                      <Link
                        href={link.href}
                        className="text-gray-600 hover:text-[#ff7043] transition-colors duration-300 flex items-center group text-sm"
                      >
                        <span className="w-0 group-hover:w-2 h-0.5 bg-[#ff7043] mr-0 group-hover:mr-2 transition-all duration-300"></span>
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}

          {/* Subscribe & Contact Section */}
          <div className="space-y-4 md:space-y-6">
            <h3 className="text-[#463a33] font-bold text-base md:text-lg relative inline-block">
              Stay Connected
              <span className="absolute -bottom-2 left-0 w-8 h-1 bg-[#ff7043]/30 rounded-full"></span>
            </h3>

            {/* Newsletter */}
            <form onSubmit={handleSubscribe} className="relative">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                disabled={isLoading}
                className="w-full px-4 py-3 pr-12 bg-white border border-gray-200 rounded-xl focus:outline-none focus:border-[#ff7043] focus:ring-1 focus:ring-[#ff7043] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <button
                type="submit"
                disabled={isLoading}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-[#ff7043] text-white rounded-lg hover:bg-[#e6683c] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : isSubscribed ? (
                  <CheckIcon className="w-4 h-4" />
                ) : (
                  <ArrowRightIcon className="w-4 h-4" />
                )}
              </button>
            </form>

            {/* Success/Error Messages */}
            {isSubscribed && (
              <p className="text-green-600 text-sm flex items-center gap-2 animate-fadeIn">
                <CheckIcon className="w-4 h-4" />
                Successfully subscribed to newsletter!
              </p>
            )}
            {errorMessage && (
              <p className="text-red-600 text-sm flex items-center gap-2 animate-fadeIn">
                <ExclamationCircleIcon className="w-4 h-4" />
                {errorMessage}
              </p>
            )}

            {/* Contact Info */}
            <div className="space-y-3 md:space-y-4 pt-2">
              <a
                href="mailto:support@relaxingroutine.com"
                className="flex items-center gap-3 text-gray-600 hover:text-[#ff7043] transition-colors group"
              >
                <div className="w-8 h-8 rounded-full bg-orange-50 flex items-center justify-center group-hover:bg-orange-100 transition-colors">
                  <EnvelopeIcon className="w-4 h-4 text-[#ff7043]" />
                </div>
                <span className="text-sm">support@relaxingroutine.com</span>
              </a>

              <a
                href="tel:9873433324"
                className="flex items-center gap-3 text-gray-600 hover:text-[#ff7043] transition-colors group"
              >
                <div className="w-8 h-8 rounded-full bg-orange-50 flex items-center justify-center group-hover:bg-orange-100 transition-colors">
                  <PhoneIcon className="w-4 h-4 text-[#ff7043]" />
                </div>
                <span className="text-sm">9873433324</span>
              </a>

              {/* Operating Address - Commented out
              <div className="flex items-start gap-3 text-gray-600 group">
                <div className="w-8 h-8 rounded-full bg-orange-50 flex items-center justify-center shrink-0">
                  <span className="text-lg">📍</span>
                </div>
                <div className="text-sm">
                  <p className="font-medium text-[#463a33]">Operating Address:</p>
                  <p>C 986, Patel Nagar, Bareilly, Uttar Pradesh, India</p>
                </div>
              </div>
              */}

              <div className="flex items-center justify-center md:justify-start gap-2 md:gap-3 pt-2">
                <p className="text-sm font-medium text-[#463a33]">
                  Follow us on
                </p>
                {socialLinks.map((social) => (
                  <a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center bg-white border border-gray-200 rounded-full shadow-sm hover:shadow-md hover:scale-110 hover:border-[#ff7043] transition-all duration-300"
                  >
                    <span className="w-4 h-4 md:w-5 md:h-5">{social.icon}</span>
                  </a>
                ))}
              </div>

              {/* Install App Button */}
              {showInstallButton && (
                <div className="pt-4">
                  <button
                    onClick={handleInstallApp}
                    className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-[#ff6b42] to-[#ff8c42] text-white px-4 py-3 rounded-xl transition-all hover:shadow-lg hover:scale-[1.02] font-medium group"
                  >
                    <ArrowDownTrayIcon className="w-5 h-5 group-hover:animate-bounce" />
                    <span>Install App</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-gray-200">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm text-center md:text-left">
              © {new Date().getFullYear()} Relaxing Routine. All rights reserved.
              <br className="md:hidden" />
              {/* <span className="hidden md:inline"> | </span>
              <span className="font-medium text-gray-600">Legal Name: Aarav Gupta</span> */}
            </p>
            <div className="flex gap-6">
              <Link
                href="/privacy"
                className="text-gray-500 hover:text-[#ff7043] text-sm transition-colors"
              >
                Privacy
              </Link>
              <Link
                href="/terms"
                className="text-gray-500 hover:text-[#ff7043] text-sm transition-colors"
              >
                Terms & Conditions
              </Link>
              {/* <Link
                href="/refund-policy"
                className="text-gray-500 hover:text-[#ff7043] text-sm transition-colors"
              >
                Refund Policy
              </Link> */}
            </div>
          </div>
        </div>
      </div>

      <style jsx global>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            max-height: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            max-height: 500px;
            transform: translateY(0);
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(-5px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-slideDown {
          animation: slideDown 0.3s ease-out forwards;
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
      `}</style>
    </footer>
  );
}
